import type { PublicRuntimeConfig } from 'app.types';
import { jwtParser } from 'domains/auth/jwtParser';
import { profileService } from 'domains/auth/profile.service';
import { logger } from 'logs';
import type { GetServerSidePropsContext, Redirect } from 'next';
import getConfig from 'next/config';

import type { AppStore } from 'redux-store';
import { JwtSlice } from 'redux-store/slices/jwt';
import { setUserAgent, setLocale } from 'redux-store/slices/ui';
import getSocialProvidersThunk from 'redux-store/thunks/profile/getSocialProvidersThunk';

import { UrlService } from '../url';

const { publicRuntimeConfig } = getConfig();
const { environmentName }: PublicRuntimeConfig = publicRuntimeConfig;

export interface IBaseServer {
  server({
    context,
    store,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): Promise<void> | void;

  setAcceptLanguage({ context }: { context: GetServerSidePropsContext }): void;
  loadSocial({ context }: { context: GetServerSidePropsContext }): void;
  setUserAgent({
    context,
    store,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): void;
  needBeAuth({ context }: { context: GetServerSidePropsContext }):
    | {
        redirect: Redirect;
      }
    | undefined;

  redirect404(): {
    redirect: Redirect;
  };
}

export abstract class BaseServerAbstract implements IBaseServer {
  protected locale = 'en';

  setAcceptLanguage({
    context,
    locale,
    store,
  }: {
    context: GetServerSidePropsContext;
    locale?: string;
    store: AppStore;
  }): void {
    context.req.headers['accept-language'] = locale || this.locale;
    context.locale = context.req.headers['accept-language'];

    const isRu = context.locale.includes('ru') && context.locale.startsWith('ru');

    store.dispatch(setLocale(isRu ? 'ru' : 'en'));
  }

  async loadSocial({
    store,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): Promise<void> {
    await store.dispatch(getSocialProvidersThunk());
  }

  async setUserAgent({
    context,
    store,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): Promise<void> {
    await store.dispatch(setUserAgent(context.req.headers['user-agent']));
  }

  async loadProfile({
    context,
    store,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): Promise<void> {
    const isAuthenticated = this.isAuthenticated({ context });

    if (isAuthenticated) {
      await profileService.getProfile({ store }, context);
    }
  }

  protected isAuthenticated({
    context,
  }: {
    context: GetServerSidePropsContext;
  }): boolean {
    return !!context.req.cookies.welltorysessionid;
  }

  needBeAuth({
    context,
    destination,
  }: {
    context: GetServerSidePropsContext;
    destination?: string;
  }):
    | {
        redirect: Redirect;
      }
    | undefined {
    const isAuthenticated = this.isAuthenticated({ context });
    const token = context.query.token as string;
    const urlService = new UrlService({ main: false });
    let redirect = '';

    if (destination) {
      redirect = `?destination=${destination}`;
    }

    if (!(isAuthenticated || token)) {
      return {
        redirect: {
          destination: urlService.getUrl(`/auth/signin/${redirect}`),
          permanent: false,
        },
      };
    }
  }

  async checkToken({
    store,
    context,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): Promise<void> {
    const token = context.query.token as string;

    if (token) {
      try {
        store.dispatch(
          JwtSlice.actions.setJwtToken({
            token: token as string,
            payload: await jwtParser(token),
          }),
        );
      } catch (e) {
        logger.error({
          key: '[PAYMENTS][JWT-PARSER]',
          error: e,
          params: {
            token,
          },
          info: {
            'user-uid': context.req.headers['x-request-id'],
            'create-date': context.req.headers['X-request-create-date'],
            application: context.req.headers['X-request-application'],
          },
        });
        store.dispatch(
          JwtSlice.actions.setJwtError({
            code: 500,
            text: (e as Error).name,
          }),
        );
      }
    }
  }

  redirect({
    destination,
    permanent = false,
  }: {
    destination: string;
    permanent?: boolean;
  }): {
    redirect: Redirect;
  } {
    return {
      redirect: {
        destination,
        permanent,
      },
    };
  }

  redirect404(): {
    redirect: Redirect;
  } {
    return this.redirect({ destination: '/404' });
  }

  async server({
    context,
    store,
  }: {
    context: GetServerSidePropsContext;
    store: AppStore;
  }): Promise<void> {
    this.setAcceptLanguage({ context, store });
    await Promise.allSettled([
      this.setUserAgent({ context, store }),
      this.loadSocial({ context, store }),
    ]);
  }
}
